import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Badge as MuiBadge, Typography, Popper, IconButton, ClickAwayListener, Button, Link } from '@mui/material'
import { useTranslation } from 'react-i18next'
import close from 'images/close.svg'
import { getDisclosures } from './../utils/disclosures-util'
import DOMPurify from 'dompurify'
import { IS_NEW_LAYOUT_ENABLED } from 'constants/layoutConstant'

const disclosures = getDisclosures()

const Badge = ({ badgeContent }) => {
    const { t } = useTranslation()
    const matchedDisclosure = Object.values(disclosures).find((disclosure) => disclosure.order === badgeContent)
    const [open, setOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)

    const handleClick = (event) => {
        if (anchorEl) {
            setAnchorEl(null)
        }

        if (IS_NEW_LAYOUT_ENABLED) {
            setAnchorEl(event.currentTarget)
            setOpen((previousOpen) => !previousOpen)
        }
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const canBeOpen = open && Boolean(anchorEl)
    const id = canBeOpen ? 'transition-popper' : undefined

    return (
        <>
            <Link
                aria-describedby={id}
                component="button"
                className="text-with-underline-item"
                aria-label={`disclosures ${badgeContent}`}
                onClick={handleClick}
                sx={
                    IS_NEW_LAYOUT_ENABLED
                        ? {
                              left: 0,
                              top: 0,
                              color: anchorEl ? 'common.link.light' : 'common.charcoal.main',
                              fontSize: '11px',
                              lineHeight: '16px',
                              letterSpacing: '0px',
                              fontWeight: '400',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              transform: 'scale(1) translate(0%, -50%)',
                              padding: '2px 6px'
                          }
                        : {
                              backgroundColor: 'background.paper',
                              borderRadius: '3px',
                              color: 'text.primary',
                              letterSpacing: '1px',
                              left: 8,
                              top: 2,
                              typography: 'decorative'
                          }
                }
            >
                {badgeContent}
            </Link>
            <Popper
                id={id}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom-start"
                disablePortal
                sx={{
                    zIndex: 1,
                    borderRadius: 2,
                    width: 'max-content',
                    maxWidth: { xs: 185, sm: 198, md: 340 },
                    border: '1px solid #6B7786',
                    backgroundColor: 'background.default',
                    padding: '10px 25px 10px 8px',
                    gap: '16px',
                    boxShadow: 'none',
                    ml: '-4px',
                    whiteSpace: 'wrap'
                }}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <Typography component="div">
                        <Typography sx={{ margin: '5px' }} color="common.charcoal.main" variant="boldBody1">
                            {matchedDisclosure.name}
                        </Typography>

                        <Typography
                            variant="regularBody1"
                            color="common.charcoal.main"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(matchedDisclosure.desc, { ADD_ATTR: ['target'] })
                            }}
                            sx={{ margin: '5px', wordWrap: 'break-word' }}
                        ></Typography>

                        <IconButton
                            aria-label="close"
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (event.key === 'Tab') {
                                    handleClose()
                                }
                            }}
                            onClick={handleClose}
                            sx={{
                                color: 'common.charcoal.main',
                                padding: '8px 5px',
                                position: 'absolute',
                                right: 0,
                                top: 0
                            }}
                            data-testid="popper-close-button"
                        >
                            <img src={close} alt={'close modal'} width={24} height={24} />
                        </IconButton>
                    </Typography>
                </ClickAwayListener>
            </Popper>
        </>
    )
}

Badge.propTypes = {
    badgeContent: PropTypes.number
}
export default Badge
